<template>
    <div ref="container" class="bell-chart">
        <svg ref="svg" :height="height" :width="width"></svg>
    </div>
</template>

<script>
    
    import {mapGetters, mapActions} from 'vuex';

    import * as d3 from 'd3';

    const MIN_HEIGHT = 400;
    const MAX_WIDTH = 1400;
    const DURATION = 500;

    export default {
        name: "bell-chart",
        props: {
            chartData: {
                type: Array,
                required: true
            },
            userPercentile: {
                type: Number,
                required: true
            },
            userLabel: {
                type: String,
                required: true
            }
        },
        data() {
            return {
                width: 0,
                height: 0,
                margin: {top: 20, right: 20, bottom: 50, left: 65},
                xScale: null,
                yScale: null,
                svg: null,
                groups: {
                    axis: {
                        x: null,
                        y: null
                    },
                    marks: null,
                    shadedAreas: null,
                    referenceLines: null
                }
            }
        },
        created() {
            this.updateWidth(window.innerWidth);
        },
        mounted() {
            // Wrap svg
            this.svg = d3.select(this.$refs.svg);
            // Do the setup
            this.init();
            // Listen for resize
            window.addEventListener('resize', this.reset);
        },
        destroyed() {
            window.removeEventListener('resize', this.reset);
        },
        computed: {
            maxClaims() {
                return this.chartData.reduce((max, item) => item > max ? item : max, 0);
            },
            ...mapGetters('windowWidth', ['theCurrentWidth'])
        },
        watch: {
            chartData() {
                this.draw();
            },
            maxClaims() {
                this.draw();
            }
        },
        methods: {
            ...mapActions('windowWidth', ['updateWidth']),
            /**
             * Setup charts
             */
            init() {
                this.determineHeightAndWidth();
                this.setupScales();
                this.setupGroups();
                this.setupAxisLabels();
                this.draw();
            },
            /**
             * Determine height and width form the parent container.
             */
            determineHeightAndWidth() {
                let container = d3.select(this.$refs.container);
                let containerWidth = parseFloat(container.style('width').replace('px', ''));
                let containerHeight = parseFloat(container.style('height').replace('px', ''));

                this.width = Math.min(MAX_WIDTH, isNaN(containerWidth) ? 500 : containerWidth);
                if(this.theCurrentWidth > 1024) {
                    this.height = Math.max(MIN_HEIGHT, isNaN(containerHeight) ? 0 : containerHeight);
                }
                else {
                    this.height = 300;
                }
            },
            /**
             * Setup the scales.
             */
            setupScales() {
                this.xScale = d3.scaleLinear()
                    .range([0, this.width - this.margin.left - this.margin.right])
                    .domain([0, 100]);
                this.yScale = d3.scaleLinear()
                    .range([this.height - this.margin.top - this.margin.bottom, 0]);
                this.applyDomainToScale();
            },
            applyDomainToScale() {
                this.yScale
                    .domain([0, this.maxClaims + 1]);
            },
            /**
             * Setup the groups and position them.
             */
            setupGroups() {
                this.svg.selectAll('g').remove();
                this.groups.axis.x = this.svg
                    .append('g')
                    .attr("transform", "translate(" + this.margin.left + "," + (this.height - this.margin.bottom) + ")")
                    .attr('class', 'x axis');
                this.groups.axis.y = this.svg
                    .append('g')
                    .attr("transform", "translate(" + this.margin.left + "," + (this.margin.top) + ")")
                    .attr('class', 'y axis');
                this.groups.backgroundShading = this.svg
                    .append('g')
                    .attr('class', 'background-shading')
                    .attr("transform", "translate(" + this.margin.left + "," + (this.margin.top) + ")");
                this.groups.shadedAreas = this.svg
                    .append('g')
                    .attr('class', 'shaded-areas')
                    .attr("transform", "translate(" + this.margin.left + "," + (this.margin.top) + ")");
                this.groups.marks = this.svg
                    .append('g')
                    .attr('class', 'marks')
                    .attr("transform", "translate(" + this.margin.left + "," + (this.margin.top) + ")");
                this.groups.referenceLines = this.svg
                    .append('g')
                    .attr('class', 'reference-lines')
                    .attr("transform", "translate(" + this.margin.left + "," + (this.margin.top) + ")");
            },
            /**
             * Apply x and y axis labels.
             */
            setupAxisLabels() {
                this.svg.append('g')
                    .attr("transform", "translate(" + this.margin.left + "," + (this.margin.top) + ")")
                    .attr('class', 'y axis-label')
                    .append('text')
                    .attr("transform", "rotate(-90)")
                    .attr("y", 0 - (this.margin.left / 2))
                    .attr("x", 0 - (this.height / 2))
                    .text("Number of Claims");
                this.svg.append('g')
                    .attr("transform", "translate(0," + (this.height - this.margin.bottom) + ")")
                    .attr('class', 'x axis-label')
                    .append('text')
                    .style("text-anchor", "middle")
                    .attr("y", this.margin.bottom)
                    .attr("x", (this.width / 2))
                    .text("Percentile");
            },
            reset() {
                this.groups.axis.x.remove();
                this.groups.axis.y.remove();
                this.groups.marks.remove();
                this.groups.shadedAreas.remove();
                this.groups.referenceLines.remove();
                if (this.$refs && this.$refs.container) {
                    this.init();
                }
            },
            /**
             * Do all drawing functions.
             */
            draw() {
                this.drawAxis();
                this.drawBackGroundShading();
                this.drawLine();
                this.drawShadedAreas();
                this.drawReferenceLines();
            },
            /**
             * Animate the axis to domain.
             */
            drawAxis() {
                this.applyDomainToScale();
                let xGroup = this.groups.axis.x;
                let yGroup = this.groups.axis.y;
                xGroup
                    .transition()
                    .duration(DURATION)
                    .call(d3.axisBottom(this.xScale)
                        .tickFormat(d => d3.format("0")(d) + '%'));
                yGroup
                    .transition()
                    .duration(DURATION)
                    .call(d3.axisLeft(this.yScale)
                        .tickFormat(d => Math.floor(d) !== d ? null : d));
            },
            drawLine() {
                this.applyDomainToScale();
                const lineValue = d3.line()
                    .x((d, i) => this.xScale(i))
                    .y(this.yScale)
                    .curve(d3.curveMonotoneX);
                const lineZero = d3.line()
                    .x((d, i) => this.xScale(i))
                    .y(() => this.yScale(0))
                    .curve(d3.curveMonotoneX);


                const line = this.groups.marks
                    .selectAll('.mark-line')
                    .data([this.chartData]);

                line.enter()
                    .append('path')
                    .attr('class', 'line mark-line')
                    .attr('d', lineZero)
                    .transition()
                    .duration(DURATION)
                    .attr('d', lineValue);

                line.transition()
                    .duration(DURATION)
                    .attr('d', lineValue);

                line.exit()
                    .remove();
            },
            drawBackGroundShading() {
                const drawRectangle = (className, start, end) => {
                    this.groups.backgroundShading
                        .append('rect')
                        .attr("class", className)
                        .attr('x', this.xScale(start))
                        .attr('y', this.yScale(this.maxClaims + 1))
                        .attr('height', this.yScale(0) - this.yScale(this.maxClaims + 1))
                        .attr('width', this.xScale(end) - this.xScale(start));
                };
                this.groups.backgroundShading.selectAll('rect').remove();
                drawRectangle('lower-percentile', 0, 25);
                drawRectangle('mid-percentile', 25, 75);
                drawRectangle('upper-percentile', 75, 100);
            },
            drawShadedAreas() {
                const areaValue = d3.area()
                    .x((d, i) => this.xScale(i))
                    .y0(this.yScale(0))
                    .y1(this.yScale)
                    .curve(d3.curveMonotoneX);
                const areaZero = d3.area()
                    .x((d, i) => this.xScale(i))
                    .y0(this.yScale(0))
                    .y1(this.yScale(0))
                    .curve(d3.curveMonotoneX);

                const lowerQuarterShade = this.groups.shadedAreas
                    .selectAll('.quartile-lower')
                    .data([this.chartData.slice(0, 26)]);
                const midQuarterShade = this.groups.shadedAreas
                    .selectAll('.quartile-mid')
                    .data([(Array(25).fill(0))
                        .concat(this.chartData.slice(25, 76))]);
                const upperQuarterShade = this.groups.shadedAreas
                    .selectAll('.quartile-upper')
                    .data([(Array(75).fill(0))
                        .concat(this.chartData.slice(75))]);

                const drawArea = (area, className) => {
                    area.enter()
                        .append('path')
                        .attr('class', 'line ' + className)
                        .attr('d', areaZero)
                        .transition()
                        .duration(DURATION)
                        .attr('d', areaValue);

                    area.transition()
                        .duration(DURATION)
                        .attr('d', areaValue);

                    area.exit()
                        .remove();
                };

                drawArea(lowerQuarterShade, 'quartile-lower');
                drawArea(midQuarterShade, 'quartile-mid');
                drawArea(upperQuarterShade, 'quartile-upper');
            },
            drawReferenceLines() {
                const lineZero = d3.area()
                    .x(this.xScale(0))
                    .y0(this.yScale(0))
                    .y1(this.yScale(this.maxClaims + 1));
                const lineValue = d3.area()
                    .x(this.xScale)
                    .y0(this.yScale(0))
                    .y1(this.yScale(this.maxClaims + 1));

                const drawLine = (className, data) => {
                    const line = this.groups.referenceLines
                        .selectAll('.' + className)
                        .data([[data]]);
                    line.enter()
                        .append("path")
                        .attr("class", className)
                        .attr("d", lineZero)
                        .transition()
                        .duration(DURATION)
                        .attr("d", lineValue);
                    line.transition()
                        .duration(DURATION)
                        .attr('d', lineValue);
                    line.exit()
                        .remove();
                };

                drawLine('lower-percentile', 25);
                drawLine('user-percentile', this.userPercentile);
                drawLine('upper-percentile', 75);

                const text = this.groups.referenceLines
                    .selectAll('text')
                    .data([[this.userPercentile]]);

                const adjustLeft = 5;
                const adjustRight = -15;
                const adjust = (() => {
                    // If very close to 25 line
                    if (this.userPercentile > 20 && this.userPercentile < 25) {
                        return adjustLeft;
                    }
                    // If very close to 75 line
                    if (this.userPercentile > 75 && this.userPercentile < 80) {
                        return adjustRight;
                    }
                    return this.userPercentile > 50 ? adjustLeft : adjustRight;
                })();

                text.enter()
                    .append('text')
                    .attr('class','label-text')
                    .data([this.userPercentile])
                    .attr("transform", "rotate(-90)")
                    .attr("x", 0 - (this.height / 2) - 30)
                    .attr("y", 0 - (this.margin.left / 2))
                    .text("Your claim: " + this.userLabel + ' percentile')
                    .transition()
                    .duration(DURATION)
                    .attr('y', this.xScale(this.userPercentile) - adjust);
                text.text("Your claim: " + this.userLabel+ ' percentile')
                    .transition()
                    .duration(DURATION)
                    .attr('y', this.xScale(this.userPercentile) - adjust);
            }
        }
    }
</script>

<style lang="scss">
    $over-colour: #d94c8b;
    $under-colour: #3df2c2;
    $centre-colour: #6b9af2;
    .bell-chart {
        font-family: 'Nunito', sans-serif;
        // All custom lines
        .line {
            fill: none; // have no fill
            stroke: black;
        }
        .axis { //Both Axis
            path { // Main axis line (these can all be applied to individual axis)
                /*stroke: none;*/
            }
            .tick { // Individual tick
                line { // The line
                    stroke: none;
                }
                text { // The text element
                    fill: #7f7f7f;
                    font-size: 8pt;
                }
            }
        }
        .x.axis { // X axis

        }
        .y.axis { // Y axis

        }
        .marks {

        }
        .background-shading {
            .lower-percentile {
                fill: lighten($under-colour, 20%);
                stroke: none;
                opacity: .3;
            }
            .mid-percentile {
                fill: lighten($centre-colour, 20%);
                stroke: none;
                opacity: .3;
            }
            .upper-percentile {
                fill: lighten($over-colour, 20%);
                stroke: none;
                opacity: .3;
            }

        }
        .shaded-areas {
            .quartile-lower {
                fill: $under-colour;
                stroke: none;
                opacity: .8;
            }
            .quartile-mid {
                fill: $centre-colour;
                stroke: none;
                opacity: .8;
            }
            .quartile-upper {
                fill: $over-colour;
                stroke: none;
                opacity: .8;
            }
        }
        .reference-lines {
            path {
                stroke: black;
            }
            .lower-percentile {
                stroke-dasharray: 4, 8;
                stroke-width: 2px;
            }
            .user-percentile {
                stroke-dasharray: 2, 3;
                stroke-width: 1px;
            }
            .upper-percentile {
                stroke-dasharray: 4, 8;
                stroke-width: 2px;
            }
            .label-text {
                font-size: 0.8em;
            }
        }
    }

</style>


// WEBPACK FOOTER //
// BellChart.vue?d87863b0